import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Search, RefreshCw, X, Save, Loader2, FileText, Globe, CalendarDays, Layers, TrendingUp, ChevronRight, PlayCircle, CheckCircle2, AlertTriangle, Info, ArrowRight, Clock, Zap, Link2, ExternalLink, Power } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'aerodeals_admin_token';
const authHeaders = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

// ═══════════════════════════════════════════════════════════════
// CHRONO DASHBOARD (overview of blog + seo pages + queue + entities)
// ═══════════════════════════════════════════════════════════════
export function ChronoDashboardTab({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/chrono/dashboard`, { headers: authHeaders() });
      setData(r.data);
    } catch (e) {
      toast.error('Errore caricamento dashboard Chrono');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <div className="flex items-center gap-2 text-zinc-500 py-10"><Loader2 className="animate-spin" size={16} /> Caricamento…</div>;

  const kpi = (label, val, sub) => (
    <div className="border border-zinc-800 bg-zinc-900/40 p-5">
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">{label}</div>
      <div className="text-3xl text-white font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{val}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );

  return (
    <div className="space-y-8" data-testid="chrono-dashboard">
      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl text-white font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Chrono SEO Engine</h2>
          <button onClick={load} className="flex items-center gap-2 text-xs text-zinc-500 hover:text-white"><RefreshCw size={12} /> Ricarica</button>
        </div>
        <p className="text-sm text-zinc-500">Panoramica dei contenuti gestiti: Articoli Blog, Pagine SEO, Calendario editoriale, Entità.</p>
      </div>

      <div>
        <h3 className="text-[10px] font-mono uppercase tracking-widest text-[#C5A059] mb-3">Blog</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {kpi('Totale', data.blog.total)}
          {kpi('Pubblicati', data.blog.published)}
          {kpi('Bozze', data.blog.draft)}
          {kpi('Da revisionare', data.blog.needs_review)}
        </div>
      </div>

      <div>
        <h3 className="text-[10px] font-mono uppercase tracking-widest text-[#C5A059] mb-3">Pagine SEO</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {kpi('Totale', data.seo_pages.total)}
          {kpi('Pubblicate', data.seo_pages.published)}
          {kpi('Bozze', data.seo_pages.draft)}
        </div>
      </div>

      <div>
        <h3 className="text-[10px] font-mono uppercase tracking-widest text-[#C5A059] mb-3">Content Queue</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {kpi('In coda', data.queue.pending)}
          {kpi('Oggi programmati', data.queue.today, data.today)}
        </div>
      </div>

      <div>
        <h3 className="text-[10px] font-mono uppercase tracking-widest text-[#C5A059] mb-3">Entità (Knowledge Graph)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {kpi('Costruttori', data.entities.makers)}
          {kpi('Modelli', data.entities.models)}
          {kpi('Categorie', data.entities.categories)}
          {kpi('Argomenti', data.entities.topics)}
        </div>
      </div>

      <div>
        <h3 className="text-[10px] font-mono uppercase tracking-widest text-[#C5A059] mb-3">Audit Contenuti</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {kpi('Totale audit', data.audit?.total || 0)}
          {kpi('Approvati', data.audit?.approved || 0)}
          {kpi('KEEP', data.audit?.by_status?.keep || 0)}
          {kpi('Da rivedere', (data.audit?.by_status?.update || 0) + (data.audit?.by_status?.merge || 0) + (data.audit?.by_status?.archive || 0))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-4 border-t border-zinc-800">
        {[
          { id: 'audit',           label: 'Audit Contenuti',           icon: Search },
          { id: 'blog',            label: 'Gestisci Articoli Blog',    icon: FileText },
          { id: 'seo',             label: 'Gestisci Pagine SEO',       icon: Globe },
          { id: 'content-queue',   label: 'Content Queue 500gg',       icon: CalendarDays },
          { id: 'plan-generator',  label: 'Genera Piano 500gg',        icon: PlayCircle },
          { id: 'entities',        label: 'Entità',                    icon: Layers },
        ].map(a => (
          <button
            key={a.id}
            onClick={() => onNavigate?.(a.id)}
            className="flex items-center justify-between p-4 border border-zinc-800 bg-zinc-900/40 hover:border-[#C5A059] transition-colors group"
            data-testid={`chrono-nav-${a.id}`}
          >
            <span className="flex items-center gap-3 text-sm text-white"><a.icon size={14} className="text-[#C5A059]" /> {a.label}</span>
            <ChevronRight size={14} className="text-zinc-600 group-hover:text-[#C5A059]" />
          </button>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SHARED: JSON content editor (blocks: p, h2, h3, ul, ol, cta)
// ═══════════════════════════════════════════════════════════════
function ContentBlocksEditor({ blocks, onChange }) {
  const update = (i, patch) => onChange(blocks.map((b, idx) => idx === i ? { ...b, ...patch } : b));
  const remove = (i) => onChange(blocks.filter((_, idx) => idx !== i));
  const add = (type) => {
    const empty = type === 'ul' || type === 'ol' ? { type, items: [''] }
      : type === 'cta' ? { type, text: '', link: '/' }
      : { type, text: '' };
    onChange([...blocks, empty]);
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const arr = [...blocks];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };

  return (
    <div className="space-y-2">
      {(blocks || []).map((b, i) => (
        <div key={i} className="border border-zinc-800 bg-zinc-950/50 p-3">
          <div className="flex items-center justify-between mb-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <span>#{i + 1} · {b.type}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => move(i, -1)} className="px-1.5 hover:text-white" data-testid={`block-up-${i}`}>↑</button>
              <button onClick={() => move(i, 1)} className="px-1.5 hover:text-white" data-testid={`block-down-${i}`}>↓</button>
              <button onClick={() => remove(i)} className="px-1.5 text-red-400 hover:text-red-300" data-testid={`block-del-${i}`}><Trash2 size={12} /></button>
            </div>
          </div>
          {(b.type === 'p' || b.type === 'h2' || b.type === 'h3') && (
            <textarea
              value={b.text || ''}
              onChange={(e) => update(i, { text: e.target.value })}
              rows={b.type === 'p' ? 3 : 1}
              className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm p-2 font-mono"
              placeholder={`Testo (${b.type}) — supporta HTML: <strong>, <a href="...">`}
              data-testid={`block-input-${i}`}
            />
          )}
          {(b.type === 'ul' || b.type === 'ol') && (
            <div className="space-y-1">
              {(b.items || []).map((it, j) => (
                <div key={j} className="flex gap-2">
                  <input
                    value={it}
                    onChange={(e) => {
                      const items = [...(b.items || [])];
                      items[j] = e.target.value;
                      update(i, { items });
                    }}
                    className="flex-1 bg-zinc-900 border border-zinc-800 text-white text-sm p-1.5 font-mono"
                    placeholder="Voce lista (HTML ok)"
                    data-testid={`block-${i}-item-${j}`}
                  />
                  <button
                    onClick={() => update(i, { items: (b.items || []).filter((_, k) => k !== j) })}
                    className="text-red-400 hover:text-red-300"
                  ><X size={12} /></button>
                </div>
              ))}
              <button
                onClick={() => update(i, { items: [...(b.items || []), ''] })}
                className="text-xs text-[#C5A059] hover:underline"
              >+ Aggiungi voce</button>
            </div>
          )}
          {b.type === 'cta' && (
            <div className="grid grid-cols-2 gap-2">
              <input value={b.text || ''} onChange={(e) => update(i, { text: e.target.value })} placeholder="Testo CTA" className="bg-zinc-900 border border-zinc-800 text-white text-sm p-1.5" />
              <input value={b.link || ''} onChange={(e) => update(i, { link: e.target.value })} placeholder="Link (/listings, /services/...)" className="bg-zinc-900 border border-zinc-800 text-white text-sm p-1.5" />
            </div>
          )}
        </div>
      ))}
      <div className="flex flex-wrap gap-2 pt-2">
        {['p', 'h2', 'h3', 'ul', 'ol', 'cta'].map(t => (
          <button
            key={t}
            onClick={() => add(t)}
            className="text-[10px] font-mono uppercase tracking-widest px-2.5 py-1.5 border border-zinc-800 text-zinc-400 hover:text-[#C5A059] hover:border-[#C5A059]"
            data-testid={`add-block-${t}`}
          >+ {t}</button>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// BLOG POSTS CRUD
// ═══════════════════════════════════════════════════════════════
export function BlogPostsTab() {
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null); // post being edited or "new"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const r = await axios.get(`${API}/admin/blog-posts`, { headers: authHeaders(), params });
      setPosts(r.data.posts || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error('Errore caricamento articoli');
    } finally { setLoading(false); }
  }, [search, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/blog/categories`).then(r => setCategories(r.data || [])).catch(() => {});
  }, []);

  const remove = async (post) => {
    if (!window.confirm(`Eliminare "${post.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/blog-posts/${post.post_id}`, { headers: authHeaders() });
      toast.success('Articolo eliminato');
      load();
    } catch { toast.error('Errore eliminazione'); }
  };

  return (
    <div className="space-y-4" data-testid="blog-posts-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl text-white font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Articoli Blog</h2>
          <p className="text-sm text-zinc-500 mt-1">{total} articoli · CRUD completo</p>
        </div>
        <button
          onClick={() => setEditing({ __new: true, status: 'draft', pinned: false, content: [], category: categories[0]?.slug || '', date: new Date().toISOString().slice(0, 10), keywords: { primary: '', secondary: [], entity: '' } })}
          className="flex items-center gap-2 bg-[#C5A059] text-black text-sm px-4 py-2 hover:bg-[#D4B271]"
          data-testid="new-blog-post"
        ><Plus size={14} /> Nuovo articolo</button>
      </div>

      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per titolo o slug…" className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm pl-9 pr-3 py-2" data-testid="blog-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-zinc-900 border border-zinc-800 text-white text-sm px-3 py-2" data-testid="blog-status-filter">
          <option value="">Tutti gli stati</option>
          <option value="published">Pubblicati</option>
          <option value="draft">Bozze</option>
          <option value="needs_review">Da revisionare</option>
        </select>
      </div>

      {loading ? <div className="text-zinc-500 flex items-center gap-2 py-8"><Loader2 className="animate-spin" size={14} /> Caricamento…</div> : (
        <div className="border border-zinc-800 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left p-3">Titolo</th>
                <th className="text-left p-3">Categoria</th>
                <th className="text-left p-3">Data</th>
                <th className="text-left p-3">Stato</th>
                <th className="text-left p-3">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {posts.map(p => (
                <tr key={p.post_id} className="border-t border-zinc-800 hover:bg-zinc-900/40" data-testid={`blog-row-${p.post_id}`}>
                  <td className="p-3">
                    <div className="text-white line-clamp-1 max-w-md">{p.title}</div>
                    <div className="text-[11px] text-zinc-500 font-mono">/{p.slug}</div>
                  </td>
                  <td className="p-3 text-zinc-400 text-xs">{p.category}</td>
                  <td className="p-3 text-zinc-500 text-xs">{fmtDate(p.date)}</td>
                  <td className="p-3">
                    <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 ${p.status === 'published' ? 'bg-green-500/10 text-green-400' : p.status === 'draft' ? 'bg-zinc-800 text-zinc-400' : 'bg-yellow-500/10 text-yellow-400'}`}>{p.status}</span>
                    {p.pinned && <span className="ml-2 text-[10px] font-mono uppercase text-amber-500">📌 pin</span>}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      <button onClick={() => setEditing(p)} className="text-[#C5A059] hover:underline text-xs flex items-center gap-1" data-testid={`edit-${p.post_id}`}><Edit size={12} /> Modifica</button>
                      <button onClick={() => remove(p)} className="text-red-400 hover:text-red-300 text-xs flex items-center gap-1"><Trash2 size={12} /> Elimina</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <BlogEditorModal
          initial={editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function BlogEditorModal({ initial, categories, onClose, onSaved }) {
  const isNew = !!initial.__new;
  const [form, setForm] = useState({
    title: initial.title || '',
    slug: initial.slug || '',
    meta_description: initial.meta_description || '',
    category: initial.category || (categories[0]?.slug || ''),
    date: initial.date || new Date().toISOString().slice(0, 10),
    read_time: initial.read_time || '',
    image: initial.image || '',
    pinned: !!initial.pinned,
    content: initial.content || [],
    status: initial.status || 'draft',
    keywords: initial.keywords || { primary: '', secondary: [], entity: '' },
    notes: initial.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const metaLen = form.meta_description.length;
  const metaValid = metaLen >= 120 && metaLen <= 165;

  const save = async () => {
    if (!form.title.trim() || !form.category) { toast.error('Titolo e categoria sono obbligatori'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (isNew) {
        await axios.post(`${API}/admin/blog-posts`, payload, { headers: authHeaders() });
        toast.success('Articolo creato');
      } else {
        await axios.put(`${API}/admin/blog-posts/${initial.post_id}`, payload, { headers: authHeaders() });
        toast.success('Articolo aggiornato');
      }
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="blog-editor-modal">
      <div className="bg-zinc-950 border border-zinc-800 max-w-4xl w-full my-8">
        <div className="flex items-center justify-between p-5 border-b border-zinc-800 sticky top-0 bg-zinc-950 z-10">
          <h3 className="text-lg text-white font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{isNew ? 'Nuovo articolo' : 'Modifica articolo'}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1 block">Titolo H1 *</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2" data-testid="blog-title" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1 block">Slug {!isNew && '(⚠️ cambia con cautela)'}</label>
              <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto dal titolo se vuoto" className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 font-mono text-sm" data-testid="blog-slug" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1 block">Categoria *</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2">
                {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1 block">
              Meta description ({metaLen}/160 char) {metaValid ? '✓' : <span className="text-red-400">— tra 120 e 165 raccomandato</span>}
            </label>
            <textarea value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} rows={2} maxLength={200} className={`w-full bg-zinc-900 border ${metaValid ? 'border-zinc-800' : 'border-red-500/40'} text-white p-2 text-sm`} data-testid="blog-meta-desc" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1 block">Data</label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1 block">Read time</label>
              <input value={form.read_time} onChange={(e) => setForm({ ...form, read_time: e.target.value })} placeholder="es. 5 min" className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1 block">Stato</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2" data-testid="blog-status">
                <option value="draft">Bozza</option>
                <option value="published">Pubblicato</option>
                <option value="needs_review">Da revisionare</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1 block">Immagine hero (URL)</label>
            <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 font-mono text-sm" />
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
              <input type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} className="w-4 h-4 accent-[#C5A059]" />
              Pinnato in cima al blog
            </label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-zinc-800 pt-4">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-[#C5A059] mb-1 block">Keyword primaria (SEO)</label>
              <input value={form.keywords?.primary || ''} onChange={(e) => setForm({ ...form, keywords: { ...form.keywords, primary: e.target.value } })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 text-sm" placeholder="es. Pilatus PC-12 prezzo" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-[#C5A059] mb-1 block">Entità principale</label>
              <input value={form.keywords?.entity || ''} onChange={(e) => setForm({ ...form, keywords: { ...form.keywords, entity: e.target.value } })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 text-sm" placeholder="es. pilatus-pc-12" />
            </div>
          </div>
          <div className="border-t border-zinc-800 pt-4">
            <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2 block">Contenuto (blocchi strutturati)</label>
            <ContentBlocksEditor blocks={form.content} onChange={(c) => setForm({ ...form, content: c })} />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-zinc-800 sticky bottom-0 bg-zinc-950">
          <button onClick={onClose} className="px-4 py-2 text-sm text-zinc-400 hover:text-white">Annulla</button>
          <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-[#C5A059] text-black text-sm px-5 py-2 hover:bg-[#D4B271] disabled:opacity-60" data-testid="blog-save-btn">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Salva
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SEO PAGES CRUD (structure identical to blog but for evergreen pages)
// ═══════════════════════════════════════════════════════════════
export function SEOPagesTab() {
  const [pages, setPages] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (search) params.search = search;
      const r = await axios.get(`${API}/admin/seo-pages`, { headers: authHeaders(), params });
      setPages(r.data.pages || []);
      setTotal(r.data.total || 0);
    } catch { toast.error('Errore caricamento pagine SEO'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const remove = async (p) => {
    if (!window.confirm(`Eliminare "${p.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/seo-pages/${p.page_id}`, { headers: authHeaders() });
      toast.success('Pagina eliminata');
      load();
    } catch { toast.error('Errore eliminazione'); }
  };

  return (
    <div className="space-y-4" data-testid="seo-pages-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl text-white font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Pagine SEO</h2>
          <p className="text-sm text-zinc-500 mt-1">{total} pagine · Evergreen (categorie, costruttori, modelli, argomenti)</p>
        </div>
        <button
          onClick={() => setEditing({ __new: true, status: 'draft', content: [], entity_type: 'topic', keywords: { primary: '', secondary: [], entity: '' } })}
          className="flex items-center gap-2 bg-[#C5A059] text-black text-sm px-4 py-2 hover:bg-[#D4B271]"
          data-testid="new-seo-page"
        ><Plus size={14} /> Nuova pagina SEO</button>
      </div>

      <div className="relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca…" className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm pl-9 pr-3 py-2" />
      </div>

      {loading ? <div className="text-zinc-500 flex items-center gap-2 py-8"><Loader2 className="animate-spin" size={14} /> Caricamento…</div>
        : pages.length === 0 ? (
          <div className="border border-zinc-800 bg-zinc-900/40 p-8 text-center text-zinc-500 text-sm">
            Nessuna pagina SEO. Inizia creando pagine per categorie principali (es. "Aerei monomotore"), costruttori (es. "Pilatus") o modelli (es. "Pilatus PC-12").
          </div>
        ) : (
          <div className="border border-zinc-800 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="text-left p-3">Titolo</th>
                  <th className="text-left p-3">Tipo</th>
                  <th className="text-left p-3">URL</th>
                  <th className="text-left p-3">Stato</th>
                  <th className="text-left p-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {pages.map(p => (
                  <tr key={p.page_id} className="border-t border-zinc-800 hover:bg-zinc-900/40" data-testid={`seo-row-${p.page_id}`}>
                    <td className="p-3 text-white line-clamp-1 max-w-md">{p.title}</td>
                    <td className="p-3 text-zinc-400 text-xs">{p.entity_type}</td>
                    <td className="p-3 text-[11px] font-mono text-zinc-500">{p.url_path || `/${p.slug}`}</td>
                    <td className="p-3">
                      <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 ${p.status === 'published' ? 'bg-green-500/10 text-green-400' : 'bg-zinc-800 text-zinc-400'}`}>{p.status}</span>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <button onClick={() => setEditing(p)} className="text-[#C5A059] hover:underline text-xs flex items-center gap-1"><Edit size={12} /> Modifica</button>
                        <button onClick={() => remove(p)} className="text-red-400 hover:text-red-300 text-xs flex items-center gap-1"><Trash2 size={12} /> Elimina</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {editing && (
        <SEOPageEditorModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function SEOPageEditorModal({ initial, onClose, onSaved }) {
  const isNew = !!initial.__new;
  const [form, setForm] = useState({
    title: initial.title || '',
    slug: initial.slug || '',
    url_path: initial.url_path || '',
    h1: initial.h1 || '',
    meta_description: initial.meta_description || '',
    entity_type: initial.entity_type || 'topic',
    entity_id: initial.entity_id || '',
    image: initial.image || '',
    content: initial.content || [],
    status: initial.status || 'draft',
    keywords: initial.keywords || { primary: '', secondary: [], entity: '' },
    notes: initial.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const metaLen = form.meta_description.length;
  const metaValid = metaLen >= 120 && metaLen <= 165;

  const save = async () => {
    if (!form.title.trim()) { toast.error('Titolo obbligatorio'); return; }
    setSaving(true);
    try {
      if (isNew) {
        await axios.post(`${API}/admin/seo-pages`, form, { headers: authHeaders() });
        toast.success('Pagina creata');
      } else {
        await axios.put(`${API}/admin/seo-pages/${initial.page_id}`, form, { headers: authHeaders() });
        toast.success('Pagina aggiornata');
      }
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="seo-editor-modal">
      <div className="bg-zinc-950 border border-zinc-800 max-w-4xl w-full my-8">
        <div className="flex items-center justify-between p-5 border-b border-zinc-800 sticky top-0 bg-zinc-950 z-10">
          <h3 className="text-lg text-white font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{isNew ? 'Nuova pagina SEO' : 'Modifica pagina SEO'}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1 block">Meta title / H1 *</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, h1: form.h1 || e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2" data-testid="seo-title" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1 block">Slug</label>
              <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto se vuoto" className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 font-mono text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1 block">URL path</label>
              <input value={form.url_path} onChange={(e) => setForm({ ...form, url_path: e.target.value })} placeholder="/pilatus-pc-12" className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 font-mono text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1 block">Tipo entità</label>
              <select value={form.entity_type} onChange={(e) => setForm({ ...form, entity_type: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2">
                <option value="category">Categoria</option>
                <option value="maker">Costruttore</option>
                <option value="model">Modello</option>
                <option value="topic">Argomento evergreen</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1 block">
              Meta description ({metaLen}/160 char) {metaValid ? '✓' : <span className="text-red-400">— tra 120 e 165 raccomandato</span>}
            </label>
            <textarea value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} rows={2} maxLength={200} className={`w-full bg-zinc-900 border ${metaValid ? 'border-zinc-800' : 'border-red-500/40'} text-white p-2 text-sm`} data-testid="seo-meta-desc" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-[#C5A059] mb-1 block">Keyword primaria</label>
              <input value={form.keywords?.primary || ''} onChange={(e) => setForm({ ...form, keywords: { ...form.keywords, primary: e.target.value } })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-[#C5A059] mb-1 block">Entity ID</label>
              <input value={form.entity_id} onChange={(e) => setForm({ ...form, entity_id: e.target.value })} placeholder="ent_model_pilatus-pc-12" className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 font-mono text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1 block">Stato</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2" data-testid="seo-status">
                <option value="draft">Bozza</option>
                <option value="published">Pubblicata</option>
                <option value="needs_review">Da revisionare</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1 block">Immagine hero (URL)</label>
            <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 font-mono text-sm" />
          </div>
          <div className="border-t border-zinc-800 pt-4">
            <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2 block">Contenuto (blocchi strutturati)</label>
            <ContentBlocksEditor blocks={form.content} onChange={(c) => setForm({ ...form, content: c })} />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-zinc-800 sticky bottom-0 bg-zinc-950">
          <button onClick={onClose} className="px-4 py-2 text-sm text-zinc-400 hover:text-white">Annulla</button>
          <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-[#C5A059] text-black text-sm px-5 py-2 hover:bg-[#D4B271] disabled:opacity-60" data-testid="seo-save-btn">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Salva
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CONTENT QUEUE — 500-day editorial calendar (list view + inline CRUD)
// ═══════════════════════════════════════════════════════════════
export function ContentQueueTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [stats, setStats] = useState(null);
  const [generating, setGenerating] = useState({}); // { queue_id: true }
  const [pollingFor, setPollingFor] = useState({}); // for status polling

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (typeFilter) params.content_type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      const [r, s] = await Promise.all([
        axios.get(`${API}/admin/content-queue`, { headers: authHeaders(), params }),
        axios.get(`${API}/admin/content-queue/stats`, { headers: authHeaders() }),
      ]);
      setItems(r.data.items || []);
      setStats(s.data);
    } catch { toast.error('Errore caricamento coda'); }
    finally { setLoading(false); }
  }, [typeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Poll generation status for items marked "generating"
  useEffect(() => {
    const generatingIds = items.filter(i => i.status === 'generating').map(i => i.queue_id);
    if (generatingIds.length === 0) return;
    const timer = setInterval(async () => {
      let updated = false;
      for (const qid of generatingIds) {
        try {
          const r = await axios.get(`${API}/admin/chrono/generate/status/${qid}`, { headers: authHeaders() });
          if (r.data.status !== 'generating') {
            updated = true;
            if (r.data.status === 'generated') toast.success('Generazione completata');
            else if (r.data.generation_error) toast.error(`Errore: ${r.data.generation_error.slice(0, 100)}`);
          }
        } catch {}
      }
      if (updated) load();
    }, 8000);
    return () => clearInterval(timer);
  }, [items, load]);

  const remove = async (item) => {
    if (!window.confirm(`Rimuovere dalla coda "${item.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/content-queue/${item.queue_id}`, { headers: authHeaders() });
      toast.success('Voce rimossa');
      load();
    } catch { toast.error('Errore rimozione'); }
  };

  const generate = async (item) => {
    if (!window.confirm(`Generare contenuto AI per "${item.title}"?\nModello: Claude Sonnet 4.6\nOutput in stato needs_review (mai auto-pubblicato).`)) return;
    try {
      setGenerating({ ...generating, [item.queue_id]: true });
      const r = await axios.post(`${API}/admin/chrono/generate/${item.queue_id}`, {}, { headers: authHeaders() });
      toast.info(r.data.message || 'Generazione avviata');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore avvio generazione');
      setGenerating({ ...generating, [item.queue_id]: false });
    }
  };

  return (
    <div className="space-y-4" data-testid="content-queue-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl text-white font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Content Queue</h2>
          <p className="text-sm text-zinc-500 mt-1">Calendario editoriale · 500 giorni · 1 Pagina SEO + 1 Articolo al giorno</p>
        </div>
        <button
          onClick={() => setEditing({ __new: true, type: 'blog_post', scheduled_date: new Date().toISOString().slice(0, 10), status: 'pending', priority: 5 })}
          className="flex items-center gap-2 bg-[#C5A059] text-black text-sm px-4 py-2 hover:bg-[#D4B271]"
          data-testid="new-queue-item"
        ><Plus size={14} /> Aggiungi in coda</button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div className="border border-zinc-800 p-3"><div className="text-[9px] text-zinc-500 uppercase tracking-widest mb-1">Oggi</div><div className="text-xl text-white">{stats.today}</div></div>
          <div className="border border-zinc-800 p-3"><div className="text-[9px] text-zinc-500 uppercase tracking-widest mb-1">In coda</div><div className="text-xl text-white">{stats.pending}</div></div>
          <div className="border border-zinc-800 p-3"><div className="text-[9px] text-zinc-500 uppercase tracking-widest mb-1">Generati</div><div className="text-xl text-white">{stats.generated}</div></div>
          <div className="border border-zinc-800 p-3"><div className="text-[9px] text-zinc-500 uppercase tracking-widest mb-1">Pubblicati</div><div className="text-xl text-white">{stats.published}</div></div>
          <div className="border border-zinc-800 p-3"><div className="text-[9px] text-zinc-500 uppercase tracking-widest mb-1">Prossimi 7gg</div><div className="text-xl text-white">{stats.upcoming_7d}</div></div>
          <div className="border border-zinc-800 p-3"><div className="text-[9px] text-zinc-500 uppercase tracking-widest mb-1">Totale</div><div className="text-xl text-white">{stats.total}</div></div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="bg-zinc-900 border border-zinc-800 text-white text-sm px-3 py-2">
          <option value="">Tutti i tipi</option>
          <option value="seo_page">Pagina SEO</option>
          <option value="blog_post">Articolo Blog</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-zinc-900 border border-zinc-800 text-white text-sm px-3 py-2">
          <option value="">Tutti gli stati</option>
          <option value="pending">In coda</option>
          <option value="generated">Generato</option>
          <option value="needs_review">Da revisionare</option>
          <option value="published">Pubblicato</option>
          <option value="blocked">Bloccato</option>
        </select>
      </div>

      {loading ? <div className="text-zinc-500 flex items-center gap-2 py-8"><Loader2 className="animate-spin" size={14} /> Caricamento…</div>
        : items.length === 0 ? (
          <div className="border border-zinc-800 bg-zinc-900/40 p-8 text-center text-zinc-500 text-sm">
            Coda vuota. Inizia aggiungendo voci di programmazione (titolo + keyword primaria + tipo + data).
          </div>
        ) : (
          <div className="border border-zinc-800 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="text-left p-3">Data</th>
                  <th className="text-left p-3">Tipo</th>
                  <th className="text-left p-3">Titolo pianificato</th>
                  <th className="text-left p-3">Keyword</th>
                  <th className="text-left p-3">Cluster</th>
                  <th className="text-left p-3">Stato</th>
                  <th className="text-left p-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <tr key={it.queue_id} className="border-t border-zinc-800 hover:bg-zinc-900/40" data-testid={`queue-row-${it.queue_id}`}>
                    <td className="p-3 text-zinc-300 font-mono text-xs">{it.scheduled_date}</td>
                    <td className="p-3"><span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 ${it.type === 'seo_page' ? 'bg-[#C5A059]/20 text-[#C5A059]' : 'bg-blue-500/10 text-blue-400'}`}>{it.type}</span></td>
                    <td className="p-3 text-white line-clamp-1 max-w-md">{it.title}</td>
                    <td className="p-3 text-zinc-500 text-xs font-mono">{it.keyword_primary}</td>
                    <td className="p-3 text-zinc-400 text-xs">{it.cluster || '—'}</td>
                    <td className="p-3"><span className="text-[10px] font-mono uppercase text-zinc-400">{it.status}</span></td>
                    <td className="p-3">
                      <div className="flex gap-2 items-center">
                        {it.status === 'pending' && (
                          <button
                            onClick={() => generate(it)}
                            disabled={generating[it.queue_id]}
                            className="text-[#C5A059] hover:underline text-xs flex items-center gap-1 disabled:opacity-60"
                            data-testid={`generate-${it.queue_id}`}
                            title="Genera contenuto AI (Claude Sonnet 4.6)"
                          >
                            <PlayCircle size={12} /> Genera
                          </button>
                        )}
                        {it.status === 'generating' && (
                          <span className="text-[#C5A059] text-xs flex items-center gap-1"><Loader2 className="animate-spin" size={12} /> in corso…</span>
                        )}
                        {it.status === 'generated' && it.content_id && (
                          <span className="text-green-400 text-xs flex items-center gap-1"><CheckCircle2 size={12} /> pronto</span>
                        )}
                        <button onClick={() => setEditing(it)} className="text-zinc-400 hover:text-white text-xs flex items-center gap-1"><Edit size={12} /></button>
                        <button onClick={() => remove(it)} className="text-red-400 hover:text-red-300 text-xs"><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {editing && (
        <QueueEditorModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function QueueEditorModal({ initial, onClose, onSaved }) {
  const isNew = !!initial.__new;
  const [form, setForm] = useState({
    scheduled_date: initial.scheduled_date || new Date().toISOString().slice(0, 10),
    type: initial.type || 'blog_post',
    title: initial.title || '',
    keyword_primary: initial.keyword_primary || '',
    keyword_secondary: initial.keyword_secondary || [],
    cluster: initial.cluster || '',
    subcluster: initial.subcluster || '',
    entity: initial.entity || '',
    category: initial.category || '',
    priority: initial.priority ?? 5,
    status: initial.status || 'pending',
    notes: initial.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.title.trim() || !form.scheduled_date) { toast.error('Titolo e data sono obbligatori'); return; }
    setSaving(true);
    try {
      if (isNew) await axios.post(`${API}/admin/content-queue`, form, { headers: authHeaders() });
      else await axios.put(`${API}/admin/content-queue/${initial.queue_id}`, form, { headers: authHeaders() });
      toast.success('Voce coda salvata');
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-zinc-950 border border-zinc-800 max-w-2xl w-full my-8">
        <div className="flex items-center justify-between p-5 border-b border-zinc-800">
          <h3 className="text-lg text-white font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{isNew ? 'Aggiungi in coda' : 'Modifica voce'}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-[10px] font-mono uppercase text-zinc-500 mb-1 block">Data programmata *</label><input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 text-sm" data-testid="q-date" /></div>
            <div><label className="text-[10px] font-mono uppercase text-zinc-500 mb-1 block">Tipo *</label><select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2" data-testid="q-type"><option value="blog_post">Articolo Blog</option><option value="seo_page">Pagina SEO</option></select></div>
          </div>
          <div><label className="text-[10px] font-mono uppercase text-zinc-500 mb-1 block">Titolo pianificato *</label><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2" data-testid="q-title" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-[10px] font-mono uppercase text-[#C5A059] mb-1 block">Keyword primaria</label><input value={form.keyword_primary} onChange={(e) => setForm({ ...form, keyword_primary: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 text-sm" /></div>
            <div><label className="text-[10px] font-mono uppercase text-[#C5A059] mb-1 block">Entità</label><input value={form.entity} onChange={(e) => setForm({ ...form, entity: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 text-sm" /></div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div><label className="text-[10px] font-mono uppercase text-zinc-500 mb-1 block">Cluster</label><input value={form.cluster} onChange={(e) => setForm({ ...form, cluster: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 text-sm" /></div>
            <div><label className="text-[10px] font-mono uppercase text-zinc-500 mb-1 block">Priorità (1-10)</label><input type="number" min="1" max="10" value={form.priority} onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 text-sm" /></div>
            <div><label className="text-[10px] font-mono uppercase text-zinc-500 mb-1 block">Stato</label><select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2"><option value="pending">In coda</option><option value="generated">Generato</option><option value="needs_review">Da revisionare</option><option value="published">Pubblicato</option><option value="blocked">Bloccato</option></select></div>
          </div>
          <div><label className="text-[10px] font-mono uppercase text-zinc-500 mb-1 block">Note</label><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 text-sm" /></div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-zinc-800">
          <button onClick={onClose} className="px-4 py-2 text-sm text-zinc-400 hover:text-white">Annulla</button>
          <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-[#C5A059] text-black text-sm px-5 py-2 hover:bg-[#D4B271]" data-testid="q-save-btn">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Salva
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ENTITIES — knowledge graph
// ═══════════════════════════════════════════════════════════════
export function EntitiesTab() {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (typeFilter) params.entity_type = typeFilter;
      if (search) params.search = search;
      const r = await axios.get(`${API}/admin/entities`, { headers: authHeaders(), params });
      setEntities(r.data.entities || []);
    } catch { toast.error('Errore caricamento entità'); }
    finally { setLoading(false); }
  }, [typeFilter, search]);
  useEffect(() => { load(); }, [load]);

  const remove = async (e) => {
    if (!window.confirm(`Eliminare "${e.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/entities/${e.entity_id}`, { headers: authHeaders() });
      toast.success('Entità eliminata');
      load();
    } catch { toast.error('Errore'); }
  };

  return (
    <div className="space-y-4" data-testid="entities-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl text-white font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Entità</h2>
          <p className="text-sm text-zinc-500 mt-1">Knowledge graph: costruttori, modelli, categorie, argomenti evergreen</p>
        </div>
        <button
          onClick={() => setEditing({ __new: true, type: 'model', aliases: [] })}
          className="flex items-center gap-2 bg-[#C5A059] text-black text-sm px-4 py-2 hover:bg-[#D4B271]"
          data-testid="new-entity"
        ><Plus size={14} /> Nuova entità</button>
      </div>

      <div className="flex gap-2 flex-wrap">
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="bg-zinc-900 border border-zinc-800 text-white text-sm px-3 py-2">
          <option value="">Tutti i tipi</option>
          <option value="maker">Costruttori</option>
          <option value="model">Modelli</option>
          <option value="category">Categorie</option>
          <option value="topic">Argomenti</option>
        </select>
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca…" className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm pl-9 pr-3 py-2" />
        </div>
      </div>

      {loading ? <div className="text-zinc-500 flex items-center gap-2 py-8"><Loader2 className="animate-spin" size={14} /> Caricamento…</div> : (
        <div className="border border-zinc-800 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left p-3">Tipo</th>
                <th className="text-left p-3">Nome</th>
                <th className="text-left p-3">Slug</th>
                <th className="text-left p-3">Parent</th>
                <th className="text-left p-3">Canonical URL</th>
                <th className="text-left p-3">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {entities.map(e => (
                <tr key={e.entity_id} className="border-t border-zinc-800 hover:bg-zinc-900/40">
                  <td className="p-3"><span className="text-[10px] font-mono uppercase text-[#C5A059]">{e.type}</span></td>
                  <td className="p-3 text-white">{e.name}</td>
                  <td className="p-3 text-[11px] text-zinc-500 font-mono">{e.slug}</td>
                  <td className="p-3 text-xs text-zinc-500 font-mono">{e.parent_slug || '—'}</td>
                  <td className="p-3 text-xs text-zinc-500 font-mono">{e.canonical_url || '—'}</td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      <button onClick={() => setEditing(e)} className="text-[#C5A059] hover:underline text-xs flex items-center gap-1"><Edit size={12} /></button>
                      <button onClick={() => remove(e)} className="text-red-400 hover:text-red-300 text-xs"><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EntityEditorModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function EntityEditorModal({ initial, onClose, onSaved }) {
  const isNew = !!initial.__new;
  const [form, setForm] = useState({
    type: initial.type || 'model',
    name: initial.name || '',
    slug: initial.slug || '',
    aliases: initial.aliases || [],
    parent_slug: initial.parent_slug || '',
    canonical_url: initial.canonical_url || '',
    seo_page_id: initial.seo_page_id || '',
    notes: initial.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.name.trim()) { toast.error('Nome obbligatorio'); return; }
    setSaving(true);
    try {
      if (isNew) await axios.post(`${API}/admin/entities`, form, { headers: authHeaders() });
      else await axios.put(`${API}/admin/entities/${initial.entity_id}`, form, { headers: authHeaders() });
      toast.success('Entità salvata');
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-zinc-950 border border-zinc-800 max-w-lg w-full my-8">
        <div className="flex items-center justify-between p-5 border-b border-zinc-800">
          <h3 className="text-lg text-white font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{isNew ? 'Nuova entità' : 'Modifica entità'}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div><label className="text-[10px] font-mono uppercase text-zinc-500 mb-1 block">Tipo</label><select disabled={!isNew} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 disabled:opacity-60"><option value="maker">Costruttore</option><option value="model">Modello</option><option value="category">Categoria</option><option value="topic">Argomento</option></select></div>
          <div><label className="text-[10px] font-mono uppercase text-zinc-500 mb-1 block">Nome *</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2" data-testid="e-name" /></div>
          <div><label className="text-[10px] font-mono uppercase text-zinc-500 mb-1 block">Slug</label><input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} disabled={!isNew} placeholder="auto se vuoto" className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 font-mono text-sm disabled:opacity-60" /></div>
          <div><label className="text-[10px] font-mono uppercase text-zinc-500 mb-1 block">Parent slug (opzionale)</label><input value={form.parent_slug} onChange={(e) => setForm({ ...form, parent_slug: e.target.value })} placeholder="es. 'pilatus' per un modello" className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 font-mono text-sm" /></div>
          <div><label className="text-[10px] font-mono uppercase text-zinc-500 mb-1 block">Canonical URL (se già esiste una pagina)</label><input value={form.canonical_url} onChange={(e) => setForm({ ...form, canonical_url: e.target.value })} placeholder="/pilatus-pc-12" className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 font-mono text-sm" /></div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-zinc-800">
          <button onClick={onClose} className="px-4 py-2 text-sm text-zinc-400 hover:text-white">Annulla</button>
          <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-[#C5A059] text-black text-sm px-5 py-2 hover:bg-[#D4B271]" data-testid="e-save-btn">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Salva
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// AUDIT CONTENUTI — analisi rule-based dei blog_posts + seo_pages esistenti
// ═══════════════════════════════════════════════════════════════
const STATUS_META = {
  keep:     { label: 'KEEP',     desc: 'Contenuto valido, mantieni',          color: 'bg-green-500/10 text-green-400 border-green-500/30' },
  update:   { label: 'UPDATE',   desc: 'Valido ma da ottimizzare',            color: 'bg-blue-500/10 text-blue-400 border-blue-500/30' },
  merge:    { label: 'MERGE',    desc: 'Sovrapposizione — consolida',         color: 'bg-amber-500/10 text-amber-400 border-amber-500/30' },
  redirect: { label: 'REDIRECT', desc: 'Sostituito da pagina più forte',      color: 'bg-purple-500/10 text-purple-400 border-purple-500/30' },
  archive:  { label: 'ARCHIVE',  desc: 'Non utile — bozza (non elimina)',     color: 'bg-red-500/10 text-red-400 border-red-500/30' },
};

export function AuditTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [approvedFilter, setApprovedFilter] = useState('');
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [bulkApproving, setBulkApproving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.target_type = typeFilter;
      if (approvedFilter) params.approved = approvedFilter === 'true';
      if (search) params.search = search;
      const r = await axios.get(`${API}/admin/chrono/audit`, { headers: authHeaders(), params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch { toast.error('Errore caricamento audit'); }
    finally { setLoading(false); }
  }, [statusFilter, typeFilter, approvedFilter, search]);
  useEffect(() => { load(); }, [load]);

  const runAudit = async () => {
    if (!window.confirm('Eseguire un nuovo audit? Sovrascrive il precedente snapshot. Nessun articolo verrà eliminato.')) return;
    setRunning(true);
    try {
      const r = await axios.post(`${API}/admin/chrono/audit/run`, {}, { headers: authHeaders() });
      setRunResult(r.data);
      toast.success(`Audit completato: ${r.data.total} contenuti analizzati`);
      load();
    } catch { toast.error('Errore esecuzione audit'); }
    finally { setRunning(false); }
  };

  const approveItem = async (audit_id) => {
    try {
      await axios.post(`${API}/admin/chrono/audit/${audit_id}/approve`, {}, { headers: authHeaders() });
      toast.success('Approvato');
      load();
    } catch { toast.error('Errore approvazione'); }
  };

  const bulkApproveAll = async () => {
    if (!window.confirm('Approvare TUTTI gli audit non ancora approvati? Le azioni saranno applicate ai contenuti (UPDATE/MERGE/REDIRECT → needs_review, ARCHIVE → draft).')) return;
    setBulkApproving(true);
    try {
      const r = await axios.post(`${API}/admin/chrono/audit/bulk-approve`, { all_pending: true }, { headers: authHeaders() });
      toast.success(`${r.data.approved} audit approvati`);
      load();
    } catch { toast.error('Errore bulk-approve'); }
    finally { setBulkApproving(false); }
  };

  const updateItem = async (audit_id, patch) => {
    try {
      const r = await axios.put(`${API}/admin/chrono/audit/${audit_id}`, patch, { headers: authHeaders() });
      toast.success('Audit aggiornato');
      setDetail(r.data);
      load();
    } catch { toast.error('Errore salvataggio'); }
  };

  return (
    <div className="space-y-4" data-testid="audit-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl text-white font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Audit Contenuti</h2>
          <p className="text-sm text-zinc-500 mt-1">Analisi dei contenuti esistenti prima di pianificare i 500 nuovi articoli · <b>{total}</b> contenuti analizzati</p>
        </div>
        <div className="flex gap-2">
          <button onClick={runAudit} disabled={running} className="flex items-center gap-2 bg-zinc-800 text-white text-sm px-4 py-2 hover:bg-zinc-700 disabled:opacity-60" data-testid="run-audit-btn">
            {running ? <Loader2 className="animate-spin" size={14} /> : <PlayCircle size={14} />} Esegui audit
          </button>
          {total > 0 && (
            <button onClick={bulkApproveAll} disabled={bulkApproving} className="flex items-center gap-2 bg-[#C5A059] text-black text-sm px-4 py-2 hover:bg-[#D4B271] disabled:opacity-60" data-testid="bulk-approve-btn">
              {bulkApproving ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />} Approva tutti
            </button>
          )}
        </div>
      </div>

      {runResult && (
        <div className="border border-zinc-800 bg-zinc-900/40 p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(STATUS_META).map(([k, m]) => (
            <div key={k} className={`border ${m.color} p-3`}>
              <div className="text-[9px] font-mono uppercase tracking-widest mb-1 opacity-70">{m.label}</div>
              <div className="text-2xl font-medium">{runResult[k] ?? 0}</div>
              <div className="text-[10px] opacity-70 mt-1">{m.desc}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-zinc-900 border border-zinc-800 text-white text-sm px-3 py-2" data-testid="audit-status-filter">
          <option value="">Tutti gli stati</option>
          {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="bg-zinc-900 border border-zinc-800 text-white text-sm px-3 py-2">
          <option value="">Tutti i tipi</option>
          <option value="blog_post">Articoli Blog</option>
          <option value="seo_page">Pagine SEO</option>
        </select>
        <select value={approvedFilter} onChange={(e) => setApprovedFilter(e.target.value)} className="bg-zinc-900 border border-zinc-800 text-white text-sm px-3 py-2">
          <option value="">Tutti</option>
          <option value="false">Non approvati</option>
          <option value="true">Approvati</option>
        </select>
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca…" className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm pl-9 pr-3 py-2" />
        </div>
      </div>

      {loading ? <div className="text-zinc-500 flex items-center gap-2 py-8"><Loader2 className="animate-spin" size={14} /> Caricamento…</div>
        : items.length === 0 ? (
          <div className="border border-zinc-800 bg-zinc-900/40 p-8 text-center text-zinc-500 text-sm">
            Nessun audit ancora eseguito. Clicca <b>Esegui audit</b> per analizzare tutti gli articoli e le pagine SEO esistenti.
          </div>
        ) : (
          <div className="border border-zinc-800 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Titolo · URL</th>
                  <th className="text-left p-3">Tipo</th>
                  <th className="text-left p-3">Score</th>
                  <th className="text-left p-3">Words</th>
                  <th className="text-left p-3">Overlap</th>
                  <th className="text-left p-3">Approvato</th>
                  <th className="text-left p-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => {
                  const meta = STATUS_META[it.final_status] || {};
                  const overlapCnt = (it.overlaps?.posts?.length || 0) + (it.overlaps?.seo_pages?.length || 0);
                  return (
                    <tr key={it.audit_id} className="border-t border-zinc-800 hover:bg-zinc-900/40" data-testid={`audit-row-${it.audit_id}`}>
                      <td className="p-3"><span className={`text-[10px] font-mono uppercase px-2 py-1 border ${meta.color}`}>{meta.label}</span></td>
                      <td className="p-3">
                        <div className="text-white line-clamp-1 max-w-md">{it.target_title}</div>
                        <div className="text-[11px] text-zinc-500 font-mono">{it.target_url}</div>
                      </td>
                      <td className="p-3 text-zinc-400 text-xs">{it.target_type}</td>
                      <td className="p-3 text-white text-xs font-mono">{it.metrics?.quality_score}/100</td>
                      <td className="p-3 text-zinc-400 text-xs font-mono">{it.metrics?.word_count}</td>
                      <td className="p-3">
                        {overlapCnt > 0 ? <span className="text-amber-400 text-xs font-mono">{overlapCnt}</span> : <span className="text-zinc-600 text-xs">—</span>}
                      </td>
                      <td className="p-3">
                        {it.approved
                          ? <CheckCircle2 size={14} className="text-green-400" />
                          : <span className="text-[10px] font-mono uppercase text-zinc-500">pending</span>}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <button onClick={() => setDetail(it)} className="text-[#C5A059] hover:underline text-xs flex items-center gap-1" data-testid={`audit-detail-${it.audit_id}`}><Info size={12} /> Dettaglio</button>
                          {!it.approved && <button onClick={() => approveItem(it.audit_id)} className="text-green-400 hover:text-green-300 text-xs flex items-center gap-1"><CheckCircle2 size={12} /> Approva</button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      {detail && <AuditDetailModal item={detail} onClose={() => setDetail(null)} onUpdate={updateItem} onApprove={approveItem} />}
    </div>
  );
}

function AuditDetailModal({ item, onClose, onUpdate, onApprove }) {
  const [final, setFinal] = useState(item.final_status);
  const [notes, setNotes] = useState(item.admin_notes || '');
  const [redirectTo, setRedirectTo] = useState(item.redirect_to || '');
  const [mergeInto, setMergeInto] = useState(item.merge_into || '');
  const meta = STATUS_META[final] || {};
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="audit-detail-modal">
      <div className="bg-zinc-950 border border-zinc-800 max-w-3xl w-full my-8">
        <div className="flex items-center justify-between p-5 border-b border-zinc-800 sticky top-0 bg-zinc-950 z-10">
          <div>
            <h3 className="text-lg text-white font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{item.target_title}</h3>
            <p className="text-xs text-zinc-500 font-mono mt-1">{item.target_url}</p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div className="border border-zinc-800 p-2"><div className="text-[9px] text-zinc-500 uppercase">Score</div><div className="text-white text-lg font-medium">{item.metrics?.quality_score}/100</div></div>
            <div className="border border-zinc-800 p-2"><div className="text-[9px] text-zinc-500 uppercase">Parole</div><div className="text-white text-lg font-medium">{item.metrics?.word_count}</div></div>
            <div className="border border-zinc-800 p-2"><div className="text-[9px] text-zinc-500 uppercase">Meta ({item.metrics?.meta_description_length})</div><div className="text-white text-lg font-medium">{item.metrics?.meta_description_ok ? '✓' : '✗'}</div></div>
            <div className="border border-zinc-800 p-2"><div className="text-[9px] text-zinc-500 uppercase">Internal links</div><div className="text-white text-lg font-medium">{item.metrics?.internal_links_count}</div></div>
          </div>

          <div>
            <div className="text-[10px] font-mono uppercase text-zinc-500 mb-1">Motivazioni</div>
            <ul className="list-disc list-inside space-y-1 text-sm text-zinc-300">
              {(item.reasons || []).map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </div>

          {(item.overlaps?.posts?.length > 0 || item.overlaps?.seo_pages?.length > 0) && (
            <div className="border border-amber-500/20 bg-amber-500/5 p-3">
              <div className="text-[10px] font-mono uppercase text-amber-400 mb-2 flex items-center gap-1"><AlertTriangle size={12} /> Sovrapposizioni</div>
              {(item.overlaps?.posts || []).map((o, i) => (
                <div key={`p-${i}`} className="text-xs text-zinc-300 mb-1">
                  <span className="text-amber-400">Post:</span> /{o.target_slug} — title {o.title_similarity}, content {o.content_similarity}
                </div>
              ))}
              {(item.overlaps?.seo_pages || []).map((o, i) => (
                <div key={`s-${i}`} className="text-xs text-zinc-300 mb-1">
                  <span className="text-purple-400">SEO Page:</span> /{o.target_slug} — title {o.title_similarity}
                </div>
              ))}
            </div>
          )}

          <div className="border-t border-zinc-800 pt-4 space-y-3">
            <div>
              <label className="text-[10px] font-mono uppercase text-zinc-500 mb-1 block">Status finale (puoi cambiare la proposta)</label>
              <select value={final} onChange={(e) => setFinal(e.target.value)} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2" data-testid="audit-final-status">
                {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label} — {m.desc}</option>)}
              </select>
              <p className={`text-[10px] mt-1 ${meta.color} inline-block px-2 py-0.5 border`}>{meta.desc}</p>
            </div>
            {final === 'redirect' && (
              <div>
                <label className="text-[10px] font-mono uppercase text-zinc-500 mb-1 block">301 → URL destinazione</label>
                <input value={redirectTo} onChange={(e) => setRedirectTo(e.target.value)} placeholder="/pilatus-pc-12" className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 font-mono text-sm" />
              </div>
            )}
            {final === 'merge' && (
              <div>
                <label className="text-[10px] font-mono uppercase text-zinc-500 mb-1 block">Merge → slug articolo destinazione</label>
                <input value={mergeInto} onChange={(e) => setMergeInto(e.target.value)} placeholder="slug-articolo-master" className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 font-mono text-sm" />
              </div>
            )}
            <div>
              <label className="text-[10px] font-mono uppercase text-zinc-500 mb-1 block">Note admin</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 text-sm" />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-zinc-800 sticky bottom-0 bg-zinc-950">
          <button onClick={onClose} className="px-4 py-2 text-sm text-zinc-400 hover:text-white">Chiudi</button>
          <button
            onClick={() => onUpdate(item.audit_id, { final_status: final, admin_notes: notes, redirect_to: redirectTo, merge_into: mergeInto })}
            className="flex items-center gap-2 text-white text-sm px-4 py-2 border border-zinc-700 hover:bg-zinc-800"
            data-testid="audit-save"
          ><Save size={14} /> Salva</button>
          {!item.approved && (
            <button
              onClick={async () => { await onApprove(item.audit_id); onClose(); }}
              className="flex items-center gap-2 bg-[#C5A059] text-black text-sm px-4 py-2 hover:bg-[#D4B271]"
              data-testid="audit-approve"
            ><CheckCircle2 size={14} /> Salva & Approva</button>
          )}
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// PLAN GENERATOR — genera 500gg di content_queue
// ═══════════════════════════════════════════════════════════════
export function PlanGeneratorTab() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [planDays, setPlanDays] = useState(500);
  const [wipe, setWipe] = useState(true);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/chrono/plan/preview`, {}, { headers: authHeaders() });
      setPreview(r.data);
    } catch { toast.error('Errore preview'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadPreview(); }, [loadPreview]);

  const generate = async () => {
    if (!window.confirm(`Generare piano editoriale di ${planDays} giorni?${wipe ? ' Il piano precedente verrà sovrascritto.' : ''}`)) return;
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/admin/chrono/plan/generate`, {
        plan_days: planDays,
        wipe_previous_plan: wipe,
      }, { headers: authHeaders() });
      setLastResult(r.data);
      toast.success(`Piano generato: ${r.data.inserted} voci inserite (${r.data.days_covered} giorni)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore generazione piano');
    } finally { setGenerating(false); }
  };

  return (
    <div className="space-y-5" data-testid="plan-generator-tab">
      <div>
        <h2 className="text-2xl text-white font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Piano Editoriale 500gg</h2>
        <p className="text-sm text-zinc-500 mt-1">Genera automaticamente la <b>Content Queue</b> per {planDays} giorni consecutivi (1 Pagina SEO + 1 Articolo/giorno), partendo dalle entità e dall'inventory marketplace.</p>
      </div>

      {loading ? <div className="text-zinc-500 flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Caricamento…</div> : preview && (
        <div className={`border p-4 ${preview.eligible ? 'border-zinc-800 bg-zinc-900/40' : 'border-amber-500/40 bg-amber-500/5'}`} data-testid="plan-preview">
          <div className="flex items-center gap-2 mb-3">
            {preview.eligible ? <CheckCircle2 size={16} className="text-green-400" /> : <AlertTriangle size={16} className="text-amber-400" />}
            <div className="text-sm text-white font-medium">{preview.eligible ? 'Pronto per generare' : 'Non pronto'}</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
            <div><div className="text-zinc-500 uppercase font-mono text-[9px] mb-1">Audit totale</div><div className="text-white text-lg">{preview.audit_total}</div></div>
            <div><div className="text-zinc-500 uppercase font-mono text-[9px] mb-1">Approvati</div><div className="text-white text-lg">{preview.audit_approved}</div></div>
            <div><div className="text-zinc-500 uppercase font-mono text-[9px] mb-1">In attesa</div><div className="text-white text-lg">{preview.audit_pending}</div></div>
          </div>
          <p className="text-xs text-zinc-400 mt-3">{preview.warning}</p>
        </div>
      )}

      <div className="border border-zinc-800 bg-zinc-900/40 p-5 space-y-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#C5A059]">Parametri di generazione</div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] font-mono uppercase text-zinc-500 mb-1 block">Giorni da pianificare</label>
            <input type="number" min="1" max="500" value={planDays} onChange={(e) => setPlanDays(Number(e.target.value))} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2" data-testid="plan-days-input" />
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm text-zinc-300 mt-6 cursor-pointer">
              <input type="checkbox" checked={wipe} onChange={(e) => setWipe(e.target.checked)} className="w-4 h-4 accent-[#C5A059]" />
              Sovrascrivi eventuale piano precedente
            </label>
          </div>
        </div>
        <div className="pt-3 border-t border-zinc-800">
          <button
            onClick={generate}
            disabled={generating || (preview && !preview.eligible)}
            className="flex items-center gap-2 bg-[#C5A059] text-black text-sm px-6 py-3 hover:bg-[#D4B271] disabled:opacity-60"
            data-testid="generate-plan-btn"
          >
            {generating ? <Loader2 className="animate-spin" size={16} /> : <PlayCircle size={16} />}
            Genera piano editoriale
          </button>
        </div>
      </div>

      {lastResult && (
        <div className="border border-green-500/30 bg-green-500/5 p-5" data-testid="plan-result">
          <div className="flex items-center gap-2 text-green-400 mb-3">
            <CheckCircle2 size={16} /> Piano generato
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><div className="text-zinc-500 uppercase font-mono text-[9px] mb-1">Voci inserite</div><div className="text-white text-2xl">{lastResult.inserted}</div></div>
            <div><div className="text-zinc-500 uppercase font-mono text-[9px] mb-1">Giorni coperti</div><div className="text-white text-2xl">{lastResult.days_covered}</div></div>
            <div><div className="text-zinc-500 uppercase font-mono text-[9px] mb-1">Pagine SEO</div><div className="text-white text-2xl">{lastResult.seo_pages_planned}</div></div>
            <div><div className="text-zinc-500 uppercase font-mono text-[9px] mb-1">Articoli Blog</div><div className="text-white text-2xl">{lastResult.blog_posts_planned}</div></div>
          </div>
          <p className="text-xs text-zinc-400 mt-3">
            <b>{lastResult.covered_keywords_excluded}</b> keyword già coperte da contenuti esistenti sono state escluse per evitare cannibalizzazione.
            Vai al tab <b>Content Queue</b> per rivedere/modificare le voci prima della pubblicazione.
          </p>
        </div>
      )}

      <div className="border border-zinc-800 bg-zinc-900/40 p-4 text-xs text-zinc-500 space-y-2">
        <p className="text-[10px] font-mono uppercase text-zinc-400">Come funziona</p>
        <ul className="list-disc list-inside space-y-1 leading-relaxed">
          <li>Il planner legge le <b>Entità</b> (categorie, costruttori, modelli, argomenti evergreen) e le pesa in base alle scorte reali del marketplace.</li>
          <li>Ogni giorno viene creata <b>1 Pagina SEO</b> (una entità) + <b>1 Articolo Blog</b> (fan-out di template per quella entità).</li>
          <li>I contenuti con audit <b>KEEP</b> o <b>UPDATE</b> escludono automaticamente le loro keyword dal piano → zero cannibalizzazione.</li>
          <li>Nessuna generazione di testo qui — le voci restano <code>pending</code> finché la <b>Fase 3</b> non attiva la generazione AI.</li>
          <li>Puoi rigenerare il piano quando aggiungi nuove entità dal tab <b>Entità</b>.</li>
        </ul>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// CRON — Daily auto-generation control panel (Phase 4)
// ═══════════════════════════════════════════════════════════════
export function ChronoCronTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [form, setForm] = useState({ enabled: false, daily_limit: 2, include_backlog: true, hour_utc: 6, minute_utc: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/chrono/cron/status`, { headers: authHeaders() });
      setData(r.data);
      const s = r.data?.settings || {};
      setForm({
        enabled: !!s.enabled,
        daily_limit: s.daily_limit ?? 2,
        include_backlog: s.include_backlog !== false,
        hour_utc: s.hour_utc ?? 6,
        minute_utc: s.minute_utc ?? 0,
      });
    } catch { toast.error('Errore caricamento stato cron'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/chrono/cron/settings`, form, { headers: authHeaders() });
      toast.success('Impostazioni cron salvate');
      load();
    } catch { toast.error('Errore salvataggio'); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    if (!window.confirm('Eseguire ora la generazione? Prenderà le voci pending dalla queue e le genererà via Claude. Può richiedere alcuni minuti.')) return;
    setRunning(true);
    try {
      const r = await axios.post(`${API}/admin/chrono/cron/run-now`, {}, { headers: authHeaders() });
      const s = r.data || {};
      if (s.status === 'success') toast.success(`Generati ${s.generated}/${s.candidates} contenuti`);
      else if (s.status === 'partial') toast.warning(`Generati ${s.generated}, falliti ${s.failed}`);
      else if (s.status === 'skipped') toast.info(s.reason || 'Skipped');
      else toast.error(`Fallimento: ${s.failed} errori`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore esecuzione'); }
    finally { setRunning(false); }
  };

  if (loading || !data) return <div className="flex items-center gap-2 text-zinc-500 py-10"><Loader2 className="animate-spin" size={16} /> Caricamento…</div>;

  const s = data.settings || {};
  const runs = data.recent_runs || [];

  return (
    <div className="space-y-5" data-testid="chrono-cron-tab">
      <div>
        <h2 className="text-2xl text-white font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Cron Auto-Generazione</h2>
        <p className="text-sm text-zinc-500 mt-1">Genera automaticamente ogni giorno le voci pending della coda editoriale. Nessuna auto-pubblicazione — tutto passa in <b>needs_review</b>.</p>
      </div>

      {/* Status */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="border border-zinc-800 p-4">
          <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Stato</div>
          <div className={`text-xl font-medium flex items-center gap-2 ${s.enabled ? 'text-green-400' : 'text-zinc-500'}`}>
            <Power size={16} /> {s.enabled ? 'ATTIVO' : 'DISATTIVO'}
          </div>
        </div>
        <div className="border border-zinc-800 p-4">
          <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Orario UTC</div>
          <div className="text-xl text-white font-mono">{String(s.hour_utc ?? 6).padStart(2, '0')}:{String(s.minute_utc ?? 0).padStart(2, '0')}</div>
          <div className="text-[10px] text-zinc-500 mt-1">≈ {String((s.hour_utc ?? 6) + 1).padStart(2, '0')}:00 CET</div>
        </div>
        <div className="border border-zinc-800 p-4">
          <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Ultima esecuzione</div>
          <div className="text-sm text-white">{s.last_run_at ? new Date(s.last_run_at).toLocaleString('it-IT') : '—'}</div>
          <div className="text-[10px] text-zinc-500 mt-1">{s.last_run_status || '—'} · generati {s.last_run_generated ?? 0}</div>
        </div>
        <div className="border border-zinc-800 p-4">
          <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Limite/giorno</div>
          <div className="text-xl text-white font-mono">{s.daily_limit ?? 2}</div>
        </div>
      </div>

      {/* Settings form */}
      <div className="border border-zinc-800 bg-zinc-900/40 p-5 space-y-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#C5A059]">Impostazioni</div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="w-5 h-5 accent-[#C5A059]" data-testid="cron-enabled-toggle" />
          <span className="text-sm text-white">Abilita generazione automatica giornaliera</span>
        </label>
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={form.include_backlog} onChange={(e) => setForm({ ...form, include_backlog: e.target.checked })} className="w-5 h-5 accent-[#C5A059]" />
          <span className="text-sm text-zinc-300">Recupera arretrati (data ≤ oggi) — se disattivato, solo la data di oggi</span>
        </label>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="text-[10px] font-mono uppercase text-zinc-500 mb-1 block">Voci al giorno</label>
            <input type="number" min="1" max="10" value={form.daily_limit} onChange={(e) => setForm({ ...form, daily_limit: Number(e.target.value) })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 text-sm" data-testid="cron-daily-limit" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-zinc-500 mb-1 block">Ora UTC</label>
            <input type="number" min="0" max="23" value={form.hour_utc} onChange={(e) => setForm({ ...form, hour_utc: Number(e.target.value) })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 text-sm" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-zinc-500 mb-1 block">Minuto UTC</label>
            <input type="number" min="0" max="59" value={form.minute_utc} onChange={(e) => setForm({ ...form, minute_utc: Number(e.target.value) })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 text-sm" />
          </div>
        </div>
        <div className="pt-3 border-t border-zinc-800 flex gap-2 flex-wrap">
          <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-[#C5A059] text-black text-sm px-5 py-2 hover:bg-[#D4B271] disabled:opacity-60" data-testid="cron-save-btn">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Salva impostazioni
          </button>
          <button onClick={runNow} disabled={running} className="flex items-center gap-2 bg-zinc-800 text-white text-sm px-5 py-2 hover:bg-zinc-700 disabled:opacity-60" data-testid="cron-run-now-btn">
            {running ? <Loader2 className="animate-spin" size={14} /> : <Zap size={14} />} Esegui ora
          </button>
        </div>
        <p className="text-[10px] text-zinc-500 leading-relaxed">
          Nota tecnica: le modifiche all'orario si applicano al prossimo riavvio del backend (lo scheduler APScheduler esegue alle 06:00 UTC di default).
          <br />La sicurezza dei contenuti è garantita: <b>nessun articolo viene mai auto-pubblicato</b> — tutti finiscono in <code className="text-[#C5A059]">needs_review</code>.
        </p>
      </div>

      {/* Recent runs */}
      <div>
        <h3 className="text-[10px] font-mono uppercase tracking-widest text-[#C5A059] mb-2">Ultime esecuzioni</h3>
        {runs.length === 0 ? (
          <div className="border border-zinc-800 bg-zinc-900/40 p-6 text-center text-zinc-500 text-sm">Nessuna esecuzione ancora — clicca <b>Esegui ora</b> per testare.</div>
        ) : (
          <div className="border border-zinc-800 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="text-left p-3">Quando</th>
                  <th className="text-left p-3">Stato</th>
                  <th className="text-left p-3">Generati</th>
                  <th className="text-left p-3">Falliti</th>
                  <th className="text-left p-3">Dettagli</th>
                </tr>
              </thead>
              <tbody>
                {runs.map(r => (
                  <tr key={r.run_id} className="border-t border-zinc-800 hover:bg-zinc-900/40">
                    <td className="p-3 text-zinc-300 text-xs font-mono">{new Date(r.started_at).toLocaleString('it-IT')}</td>
                    <td className="p-3">
                      <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 ${r.status === 'success' ? 'bg-green-500/10 text-green-400' : r.status === 'partial' ? 'bg-yellow-500/10 text-yellow-400' : r.status === 'skipped' ? 'bg-zinc-800 text-zinc-400' : 'bg-red-500/10 text-red-400'}`}>{r.status}</span>
                      {r.forced && <span className="ml-2 text-[10px] text-[#C5A059]">manuale</span>}
                    </td>
                    <td className="p-3 text-green-400 font-mono">{r.generated}</td>
                    <td className="p-3 text-red-400 font-mono">{r.failed}</td>
                    <td className="p-3 text-xs text-zinc-500">{r.reason || `${(r.results || []).length} voci processate`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// REDIRECTS — 301 manager (Phase 4)
// ═══════════════════════════════════════════════════════════════
export function ChronoRedirectsTab() {
  const [items, setItems] = useState([]);
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ source_slug: '', source_type: 'blog_post', target_url: '', notes: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([
        axios.get(`${API}/admin/chrono/redirects`, { headers: authHeaders() }),
        axios.get(`${API}/admin/chrono/audit`, { params: { status: 'redirect', limit: 500 }, headers: authHeaders() }),
      ]);
      setItems(r.data.redirects || []);
      setAudits(a.data.items || []);
    } catch { toast.error('Errore caricamento redirects'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.source_slug.trim() || !form.target_url.trim()) { toast.error('Slug sorgente e URL destinazione obbligatori'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/chrono/redirects`, form, { headers: authHeaders() });
      toast.success('Redirect creato');
      setForm({ source_slug: '', source_type: 'blog_post', target_url: '', notes: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setCreating(false); }
  };

  const remove = async (rd) => {
    if (!window.confirm(`Rimuovere redirect ${rd.source_slug} → ${rd.target_url}?`)) return;
    try {
      await axios.delete(`${API}/admin/chrono/redirects/${rd.redirect_id}`, { headers: authHeaders() });
      toast.success('Redirect rimosso');
      load();
    } catch { toast.error('Errore'); }
  };

  const applyAudit = async (audit_id) => {
    try {
      const r = await axios.post(`${API}/admin/chrono/redirects/apply-audit/${audit_id}`, {}, { headers: authHeaders() });
      if (r.data.created) toast.success('Redirect applicato da audit');
      else toast.info(r.data.message || 'Già presente');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const audit_pending = audits.filter(a => !items.some(rd => rd.source_slug === a.target_slug && rd.source_type === a.target_type));

  return (
    <div className="space-y-5" data-testid="chrono-redirects-tab">
      <div>
        <h2 className="text-2xl text-white font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Gestione Redirect 301</h2>
        <p className="text-sm text-zinc-500 mt-1">Reindirizzamenti permanenti per articoli/pagine sostituite. L'articolo sorgente viene messo in <code>draft</code> e le richieste API restituiscono <code>{'{'} redirect: true {'}'}</code> — il frontend fa il vero 301.</p>
      </div>

      {/* Audit-based pending redirects */}
      {audit_pending.length > 0 && (
        <div className="border border-purple-500/30 bg-purple-500/5 p-4">
          <div className="text-[10px] font-mono uppercase text-purple-400 mb-3 flex items-center gap-2">
            <AlertTriangle size={12} /> {audit_pending.length} redirect suggeriti dall'audit — clicca per applicare
          </div>
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {audit_pending.map(a => (
              <div key={a.audit_id} className="flex items-center justify-between gap-3 border border-purple-500/20 p-2 text-xs" data-testid={`audit-redirect-${a.audit_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-white line-clamp-1">{a.target_title}</div>
                  <div className="text-zinc-500 font-mono line-clamp-1">/{a.target_slug} → {a.redirect_to || <span className="text-red-400">missing target</span>}</div>
                </div>
                {a.redirect_to ? (
                  <button onClick={() => applyAudit(a.audit_id)} className="bg-[#C5A059] text-black text-xs px-3 py-1.5 hover:bg-[#D4B271] flex items-center gap-1" data-testid={`apply-audit-${a.audit_id}`}>
                    <ArrowRight size={12} /> Applica
                  </button>
                ) : (
                  <span className="text-[10px] text-red-400">manca target</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Manual create form */}
      <div className="border border-zinc-800 bg-zinc-900/40 p-5 space-y-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#C5A059]">Aggiungi redirect manuale</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] font-mono uppercase text-zinc-500 mb-1 block">Tipo</label>
            <select value={form.source_type} onChange={(e) => setForm({ ...form, source_type: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 text-sm">
              <option value="blog_post">Articolo Blog</option>
              <option value="seo_page">Pagina SEO</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-zinc-500 mb-1 block">Slug sorgente *</label>
            <input value={form.source_slug} onChange={(e) => setForm({ ...form, source_slug: e.target.value })} placeholder="vecchio-slug" className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 font-mono text-sm" data-testid="rd-source-slug" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-zinc-500 mb-1 block">URL destinazione *</label>
            <input value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} placeholder="/blog/nuovo-slug" className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 font-mono text-sm" data-testid="rd-target-url" />
          </div>
          <div className="flex items-end">
            <button onClick={create} disabled={creating} className="w-full flex items-center justify-center gap-2 bg-[#C5A059] text-black text-sm px-4 py-2 hover:bg-[#D4B271] disabled:opacity-60" data-testid="rd-create-btn">
              {creating ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Crea
            </button>
          </div>
        </div>
      </div>

      {/* Existing redirects */}
      {loading ? <div className="text-zinc-500 flex items-center gap-2 py-6"><Loader2 className="animate-spin" size={14} /> Caricamento…</div>
        : items.length === 0 ? (
          <div className="border border-zinc-800 bg-zinc-900/40 p-8 text-center text-zinc-500 text-sm">
            Nessun redirect attivo. Crea manualmente sopra o applica quelli suggeriti dall'audit.
          </div>
        ) : (
          <div className="border border-zinc-800 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="text-left p-3">Tipo</th>
                  <th className="text-left p-3">Sorgente</th>
                  <th className="text-left p-3"></th>
                  <th className="text-left p-3">Destinazione</th>
                  <th className="text-left p-3">Codice</th>
                  <th className="text-left p-3">Creato</th>
                  <th className="text-left p-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {items.map(rd => (
                  <tr key={rd.redirect_id} className="border-t border-zinc-800 hover:bg-zinc-900/40" data-testid={`redirect-row-${rd.redirect_id}`}>
                    <td className="p-3"><span className="text-[10px] font-mono uppercase text-[#C5A059]">{rd.source_type}</span></td>
                    <td className="p-3 text-zinc-300 font-mono text-xs">/{rd.source_slug}</td>
                    <td className="p-3 text-zinc-500"><ArrowRight size={12} /></td>
                    <td className="p-3 text-white font-mono text-xs">{rd.target_url}</td>
                    <td className="p-3 text-green-400 font-mono text-xs">{rd.status_code}</td>
                    <td className="p-3 text-zinc-500 text-xs">{fmtDate(rd.created_at)}</td>
                    <td className="p-3">
                      <button onClick={() => remove(rd)} className="text-red-400 hover:text-red-300 text-xs" data-testid={`rd-delete-${rd.redirect_id}`}><Trash2 size={12} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ANDAMENTO — Simplified "just watch it" dashboard (Phase 4.1)
// ═══════════════════════════════════════════════════════════════
export function ChronoAndamentoTab({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/chrono/andamento`, { headers: authHeaders() });
      setData(r.data);
    } catch { toast.error('Errore caricamento andamento'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const bootstrap = async () => {
    if (!window.confirm(
      'Configurazione iniziale:\n\n' +
      '1. Analizza gli articoli blog esistenti (audit)\n' +
      '2. Genera il calendario editoriale 500 giorni\n\n' +
      'Richiede 5-15 secondi. Da fare UNA SOLA VOLTA al primo utilizzo. Confermi?'
    )) return;
    setBootstrapping(true);
    try {
      const r = await axios.post(`${API}/admin/chrono/bootstrap`, {}, { headers: authHeaders() });
      const audit_n = r.data?.audit?.total || r.data?.audit?.analyzed || 0;
      const plan_n = r.data?.plan?.items_created || r.data?.plan?.total_items || 0;
      toast.success(`Setup completato: ${audit_n} articoli auditati, ${plan_n} voci in coda per i prossimi 500 giorni`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore setup'); }
    finally { setBootstrapping(false); }
  };

  const toggleSystem = async () => {
    if (!data) return;
    const nextEnabled = !data.system.enabled;
    setToggling(true);
    try {
      await axios.put(`${API}/admin/chrono/cron/settings`, { enabled: nextEnabled, auto_publish: true }, { headers: authHeaders() });
      toast.success(nextEnabled ? 'Sistema ATTIVATO — pubblicherà automaticamente ogni giorno' : 'Sistema disattivato');
      load();
    } catch { toast.error('Errore'); }
    finally { setToggling(false); }
  };

  const runNow = async () => {
    if (!window.confirm('Eseguire ora la generazione + pubblicazione? Può richiedere 1-3 minuti.')) return;
    setRunningNow(true);
    try {
      const r = await axios.post(`${API}/admin/chrono/cron/run-now`, {}, { headers: authHeaders() });
      const s = r.data || {};
      if (s.status === 'success') toast.success(`Pubblicati ${s.generated} contenuti`);
      else if (s.status === 'partial') toast.warning(`OK ${s.generated}, falliti ${s.failed}`);
      else if (s.status === 'skipped') toast.info(s.reason || 'Nessuna voce da generare oggi');
      else toast.error(`Errori: ${s.failed}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setRunningNow(false); }
  };

  if (loading || !data) return <div className="flex items-center gap-2 text-zinc-500 py-10"><Loader2 className="animate-spin" size={16} /> Caricamento…</div>;

  const { system, totals, chart_30d, upcoming_7d, last_published, recent_errors } = data;
  const maxBar = Math.max(1, ...chart_30d.map(d => d.total));
  const daysActive = chart_30d.filter(d => d.total > 0).length;

  return (
    <div className="space-y-6" data-testid="chrono-andamento-tab">
      {/* First-time setup warning */}
      {totals.pending_queue === 0 && (
        <div className="border border-yellow-500/40 bg-yellow-500/5 p-6" data-testid="bootstrap-warning">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-widest text-yellow-400 mb-2 flex items-center gap-2">
                <AlertTriangle size={12} /> Configurazione iniziale necessaria
              </div>
              <h3 className="text-xl text-white font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Calendario editoriale vuoto</h3>
              <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
                Prima che il sistema possa pubblicare automaticamente devi generare <b className="text-white">una sola volta</b> il piano dei 500 giorni: verranno auditati gli articoli blog esistenti e create le voci giornaliere in coda (~1.000 slot totali).
              </p>
            </div>
            <button
              onClick={bootstrap}
              disabled={bootstrapping}
              className="flex items-center gap-2 bg-yellow-500 text-black px-5 py-3 text-sm font-medium hover:bg-yellow-400 disabled:opacity-60"
              data-testid="bootstrap-btn"
            >
              {bootstrapping ? <Loader2 className="animate-spin" size={16} /> : <PlayCircle size={16} />}
              Genera piano 500 giorni
            </button>
          </div>
        </div>
      )}

      {/* HERO — ON/OFF */}
      <div className={`border p-6 ${system.enabled ? 'border-green-500/40 bg-green-500/5' : 'border-zinc-800 bg-zinc-900/40'}`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Sistema Automatico</div>
            <div className={`text-4xl font-medium flex items-center gap-3 ${system.enabled ? 'text-green-400' : 'text-zinc-400'}`} style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              <Power size={30} /> {system.enabled ? 'ATTIVO' : 'DISATTIVO'}
            </div>
            <p className="text-sm text-zinc-400 mt-3 max-w-xl">
              {system.enabled
                ? <>Il Chrono SEO Engine genera e <b className="text-white">pubblica automaticamente</b> {system.daily_limit} contenuti ogni giorno alle {String(system.hour_utc).padStart(2,'0')}:00 UTC (≈ {String(system.hour_utc + 1).padStart(2,'0')}:00 in Italia). Non devi fare nulla.</>
                : <>Attiva il sistema per pubblicare automaticamente {system.daily_limit} nuovi contenuti SEO al giorno. Ogni articolo passa il fact-check e viene arricchito con internal linking prima della pubblicazione.</>}
            </p>
            {system.last_run_at && (
              <p className="text-[11px] text-zinc-500 mt-2 font-mono">
                Ultima esecuzione: {new Date(system.last_run_at).toLocaleString('it-IT')} · {system.last_run_status} · pubblicati {system.last_run_generated}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <button
              onClick={toggleSystem}
              disabled={toggling}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium disabled:opacity-60 ${system.enabled ? 'bg-zinc-800 text-white hover:bg-zinc-700' : 'bg-[#C5A059] text-black hover:bg-[#D4B271]'}`}
              data-testid="andamento-toggle-btn"
            >
              {toggling ? <Loader2 className="animate-spin" size={16} /> : <Power size={16} />}
              {system.enabled ? 'Disattiva' : 'Attiva sistema'}
            </button>
            <button
              onClick={runNow}
              disabled={runningNow}
              className="flex items-center gap-2 px-4 py-2 text-xs text-zinc-400 border border-zinc-700 hover:text-white hover:border-white disabled:opacity-60"
              data-testid="andamento-run-now-btn"
            >
              {runningNow ? <Loader2 className="animate-spin" size={12} /> : <Zap size={12} />} Esegui ora
            </button>
          </div>
        </div>
      </div>

      {/* KPI ROW */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border border-zinc-800 bg-zinc-900/40 p-5">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Pubblicati (30gg)</div>
          <div className="text-3xl text-white font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{totals.published_last_30_days}</div>
          <div className="text-[10px] text-zinc-500 mt-1">{daysActive}/30 giorni attivi</div>
        </div>
        <div className="border border-zinc-800 bg-zinc-900/40 p-5">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Totale pubblicati</div>
          <div className="text-3xl text-white font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{totals.published_all}</div>
          <div className="text-[10px] text-zinc-500 mt-1">{totals.published_blog} blog · {totals.published_seo} pagine SEO</div>
        </div>
        <div className="border border-zinc-800 bg-zinc-900/40 p-5">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">In coda</div>
          <div className="text-3xl text-white font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{totals.pending_queue}</div>
          <div className="text-[10px] text-zinc-500 mt-1">giorni futuri programmati</div>
        </div>
        <div className="border border-zinc-800 bg-zinc-900/40 p-5">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Da revisionare</div>
          <div className="text-3xl text-white font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{totals.generated_needs_review}</div>
          <div className="text-[10px] text-zinc-500 mt-1">generati non pubblicati</div>
        </div>
      </div>

      {/* CHART */}
      <div className="border border-zinc-800 bg-zinc-900/40 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm text-white font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Pubblicazioni ultimi 30 giorni</h3>
          <div className="flex items-center gap-3 text-[10px] font-mono uppercase text-zinc-500">
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#C5A059]"></span> Pagine SEO</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-400"></span> Blog</span>
          </div>
        </div>
        <div className="flex items-end gap-1 h-32">
          {chart_30d.map((d, i) => (
            <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group relative" title={`${d.date}: ${d.total} pubblicazioni`}>
              <div className="w-full flex flex-col justify-end" style={{ height: '100%' }}>
                {d.seo > 0 && <div className="w-full bg-[#C5A059]" style={{ height: `${(d.seo / maxBar) * 100}%` }}></div>}
                {d.blog > 0 && <div className="w-full bg-blue-400" style={{ height: `${(d.blog / maxBar) * 100}%` }}></div>}
              </div>
              {i % 5 === 0 && <div className="text-[8px] text-zinc-600 font-mono">{d.date.slice(8, 10)}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* LAST PUBLISHED + UPCOMING side-by-side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="border border-zinc-800 bg-zinc-900/40 p-5">
          <h3 className="text-sm text-white font-medium mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Ultimi pubblicati</h3>
          {last_published.length === 0 ? (
            <div className="text-zinc-500 text-sm">Nessuna pubblicazione ancora. Attiva il sistema o clicca "Esegui ora".</div>
          ) : (
            <div className="space-y-2">
              {last_published.map(p => (
                <a key={p.id} href={p.url} target="_blank" rel="noreferrer" className="flex items-start gap-3 p-2 hover:bg-zinc-900 border-l-2 border-transparent hover:border-[#C5A059]">
                  <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 mt-0.5 ${p.kind === 'blog' ? 'bg-blue-500/10 text-blue-400' : 'bg-[#C5A059]/20 text-[#C5A059]'}`}>{p.kind}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white line-clamp-1">{p.title}</div>
                    <div className="text-[10px] text-zinc-500 font-mono">{p.when ? fmtDate(p.when) : '—'} · {p.url}</div>
                  </div>
                  <ExternalLink size={12} className="text-zinc-600 mt-1" />
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="border border-zinc-800 bg-zinc-900/40 p-5">
          <h3 className="text-sm text-white font-medium mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Prossimi 7 giorni</h3>
          {upcoming_7d.length === 0 ? (
            <div className="text-zinc-500 text-sm">Nessuna voce programmata nei prossimi 7 giorni.</div>
          ) : (
            <div className="space-y-2">
              {upcoming_7d.slice(0, 8).map(u => (
                <div key={u.queue_id} className="flex items-start gap-3 p-2 border-l-2 border-transparent hover:border-zinc-600">
                  <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 mt-0.5 ${u.type === 'blog_post' ? 'bg-blue-500/10 text-blue-400' : 'bg-[#C5A059]/20 text-[#C5A059]'}`}>{u.type === 'blog_post' ? 'blog' : 'seo'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white line-clamp-1">{u.title}</div>
                    <div className="text-[10px] text-zinc-500 font-mono">{u.scheduled_date} · {u.keyword_primary || '—'}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ERRORS */}
      {recent_errors.length > 0 && (
        <div className="border border-red-500/30 bg-red-500/5 p-5">
          <h3 className="text-sm text-red-400 font-medium mb-3 flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            <AlertTriangle size={14} /> Errori recenti ({recent_errors.length})
          </h3>
          <div className="space-y-1">
            {recent_errors.slice(0, 5).map((e, i) => (
              <div key={i} className="text-xs text-zinc-400 font-mono">
                <span className="text-red-400">{new Date(e.when).toLocaleString('it-IT')}</span> — {(e.error || '').slice(0, 160)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer link to advanced */}
      <div className="text-center pt-2">
        <button onClick={() => onNavigate?.('chrono-cron')} className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-[#C5A059]" data-testid="andamento-advanced-link">
          Impostazioni avanzate →
        </button>
      </div>
    </div>
  );
}

